import { CheckCircleFilled, CloseCircleOutlined, MailOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Descriptions, Result, Space, Spin, Tag, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import BrandLogo from 'assets/img/workplansiq-logo.svg';
import { APP_ROUTE_PREFIX, RouteConstant } from 'routes/routesConstant';
import {
  getMockInvitation,
  respondToMockInvitation,
} from 'services/tenant/tenantMembershipService';
import LocalStorageUtils from 'utils/LocalStorage';

const STATUS_COLORS = {
  PENDING: 'gold', ACCEPTED: 'green', DECLINED: 'default', EXPIRED: 'red', REVOKED: 'red',
};

export default function InvitationPage() {
  const { invitationId } = useParams();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null);
  const [error, setError] = useState(null);
  const isAuthenticated = Boolean(LocalStorageUtils.jwtToken.get());

  useEffect(() => {
    if (!isAuthenticated) return;
    setInvitation(getMockInvitation(invitationId));
    setLoading(false);
  }, [invitationId, isAuthenticated]);

  if (!isAuthenticated) {
    const returnPath = `/invitation/${encodeURIComponent(invitationId)}`;
    return <Navigate to={`/?redirectTo=${encodeURIComponent(returnPath)}`} replace />;
  }

  const respond = (decision) => {
    setSubmitting(decision);
    setError(null);
    try {
      const result = respondToMockInvitation(invitationId, decision);
      setInvitation(result.invitation);
      if (decision === 'ACCEPTED') {
        navigate(`${APP_ROUTE_PREFIX}/${RouteConstant.dashboard}`, { replace: true });
      }
    } catch (responseError) {
      setError(responseError?.message || 'Unable to process this invitation.');
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <main className="invitation-page">
      <Card className="invitation-card">
        <img src={BrandLogo} alt="WorkPlansIQ" className="invitation-logo" />
        {loading ? (
          <div className="invitation-loading"><Spin size="large" /><span>Loading invitation...</span></div>
        ) : !invitation ? (
          <Result
            status="404"
            title="Invitation not found"
            subTitle="This invitation link is invalid or no longer available."
            extra={<Button onClick={() => navigate(`${APP_ROUTE_PREFIX}/${RouteConstant.tenantSelection}?mode=manage`)}>Manage Organizations</Button>}
          />
        ) : (
          <section className="invitation-content">
            <MailOutlined className="invitation-icon" />
            <Typography.Text className="tenant-selection-eyebrow">ORGANIZATION INVITATION</Typography.Text>
            <Typography.Title level={2}>You’re invited to join {invitation.organizationName}</Typography.Title>
            <Typography.Paragraph type="secondary">Review the organization and role before responding.</Typography.Paragraph>

            {error ? <Alert type="error" showIcon message={error} /> : null}

            <Descriptions bordered column={1} size="middle">
              <Descriptions.Item label="Organization"><strong>{invitation.organizationName}</strong></Descriptions.Item>
              <Descriptions.Item label="Invited email">{invitation.inviteeEmail}</Descriptions.Item>
              <Descriptions.Item label="Offered role"><Tag color="blue">{invitation.role}</Tag></Descriptions.Item>
              <Descriptions.Item label="Invited by">{invitation.invitedBy}</Descriptions.Item>
              <Descriptions.Item label="Expires">{new Date(invitation.expiresAt).toLocaleString()}</Descriptions.Item>
              <Descriptions.Item label="Status"><Tag color={STATUS_COLORS[invitation.status]}>{invitation.status}</Tag></Descriptions.Item>
            </Descriptions>

            {invitation.status === 'PENDING' ? (
              <Space wrap className="invitation-actions">
                <Button type="primary" size="large" icon={<CheckCircleFilled />} loading={submitting === 'ACCEPTED'} disabled={Boolean(submitting)} onClick={() => respond('ACCEPTED')}>Accept Invitation</Button>
                <Button size="large" danger icon={<CloseCircleOutlined />} loading={submitting === 'DECLINED'} disabled={Boolean(submitting)} onClick={() => respond('DECLINED')}>Decline</Button>
              </Space>
            ) : (
              <Button className="invitation-actions" onClick={() => navigate(`${APP_ROUTE_PREFIX}/${RouteConstant.tenantSelection}?mode=manage`)}>Manage Organizations</Button>
            )}
          </section>
        )}
      </Card>
    </main>
  );
}
